import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { FileText, UploadCloud, Check, Loader2 } from "lucide-react";

const STEPS = ["Pracovné skúsenosti", "Vzdelanie", "Schopnosti", "Jazyky", "Kompetencie"];

export default function UploadCV() {
  const nav = useNavigate();
  const inputRef = useRef();
  const [file, setFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [done, setDone] = useState(0);

  const pick = (f) => { if (f) setFile(f); };

  const analyze = async () => {
    if (!file) { inputRef.current?.click(); return; }
    setProcessing(true);
    setDone(0);
    const timer = setInterval(() => setDone((d) => Math.min(d + 1, STEPS.length - 1)), 900);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/candidate/upload-cv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      clearInterval(timer);
      setDone(STEPS.length);
      toast.success("Career Passport je hotový!");
      setTimeout(() => nav("/app/home"), 700);
    } catch (err) {
      clearInterval(timer);
      setProcessing(false);
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="min-h-screen mx-auto w-full max-w-md px-6 pt-14 pb-10 flex flex-col">
      <h1 className="font-head text-3xl font-bold tracking-tight">Vytvor svoj Career Passport</h1>
      <p className="mt-2 text-slate-600">Nahraj svoje staré CV. Zvyšok urobí umelá inteligencia.</p>

      {!processing ? (
        <>
          <div
            data-testid="cv-dropzone"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files?.[0]); }}
            className="mt-8 rounded-3xl border-2 border-dashed border-slate-300 bg-white p-10 flex flex-col items-center text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50/40 transition-colors"
          >
            {file ? (
              <>
                <div className="h-16 w-16 rounded-2xl bg-blue-50 flex items-center justify-center"><FileText className="text-blue-700" size={30} /></div>
                <p className="mt-4 font-semibold text-slate-800 break-all">{file.name}</p>
                <p className="text-sm text-slate-500 mt-1">Ťuknite pre zmenu súboru</p>
              </>
            ) : (
              <>
                <div className="h-16 w-16 rounded-2xl bg-blue-50 flex items-center justify-center"><UploadCloud className="text-blue-700" size={30} /></div>
                <p className="mt-4 font-semibold text-slate-800">Ťuknite a nahrajte CV</p>
                <p className="text-sm text-slate-500 mt-1">PDF, DOCX alebo TXT</p>
              </>
            )}
          </div>
          <input ref={inputRef} type="file" accept=".pdf,.docx,.doc,.txt" hidden data-testid="cv-file-input"
            onChange={(e) => pick(e.target.files?.[0])} />

          <button data-testid="analyze-cv-btn" onClick={analyze}
            className="mt-auto w-full bg-blue-700 text-white hover:bg-blue-800 rounded-full px-6 py-4 font-semibold transition-colors active:scale-95">
            {file ? "Analyzovať CV" : "Vybrať súbor"}
          </button>
        </>
      ) : (
        <div className="mt-10 flex-1 flex flex-col items-center" data-testid="cv-processing">
          <div className="relative overflow-hidden animate-scan h-36 w-28 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center">
            <FileText className="text-blue-700" size={44} />
          </div>
          <p className="mt-6 font-head text-xl font-semibold flex items-center gap-2">
            <Loader2 className="animate-spin text-blue-700" size={20} /> Analyzujem profil…
          </p>
          <div className="mt-6 w-full space-y-3">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-center gap-3">
                <div className={`h-6 w-6 rounded-full flex items-center justify-center transition-colors ${i < done ? "bg-emerald-500" : "bg-slate-200"}`}>
                  {i < done ? <Check className="text-white" size={14} /> : <span className="h-2 w-2 rounded-full bg-slate-400" />}
                </div>
                <span className={`text-sm ${i < done ? "text-slate-800 font-medium" : "text-slate-400"}`}>{s}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
